import { Card, CardContent } from '@/app/common/components/Card'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { Button } from '@/app/common/components/button/Button'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { Check, Eye, Store, X } from 'lucide-react'
import { useState } from 'react'
import AdminAuthGuard from '../../../admin-auth-guard'
import { useGeneratePreviewToken } from '../hooks/useGeneratePreviewToken'
import { useSubmitPreviewSettings } from '../hooks/useSubmitStore'

type UnpublishedChangesBannerProps = {
  providerId: string
  programId: number
  providerPreviewInformation: ProviderPreviewSettings
}
const UnpublishedChangesBanner = ({
  providerId,
  programId,
  providerPreviewInformation,
}: UnpublishedChangesBannerProps) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const previewTokenMutation = useGeneratePreviewToken()

  const handlePreviewClick = () => {
    previewTokenMutation.mutate({
      programId,
      providerId,
    })
  }

  const submitMutation = useSubmitPreviewSettings({
    providerId,
  })

  const handlePublishClick = () => {
    submitMutation.mutate(programId, {
      onSuccess: () => {
        setDialogOpen(false)
      },
    })
  }

  if (!providerPreviewInformation.updated_at) {
    return null
  }

  return (
    <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
      <Card className="w-full border border-gray-200 bg-white/70 shadow-sm">
        <CardContent className="grid grid-cols-2 items-center gap-6 py-4">
          <div className="min-w-0">
            <p className="font-medium text-gray-900">
              You have unpublished changes to your marketplace. <br />
              Publish your changes to make them visible to customers.
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Your updates are saved as drafts until you publish them.
            </p>
          </div>

          <div className="justify-self-end">
            <div className="flex items-center justify-center gap-3">
              <SubmitButton
                variant="outline"
                onClick={handlePreviewClick}
                status={previewTokenMutation.status}
                aria-label="preview_changes"
                size="md"
                className="w-auto"
                submittingText="Loading"
                buttonTxt={
                  <>
                    Preview Changes
                    <Eye className="h-4 w-4 ml-2" />
                  </>
                }
              />
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" aria-label="publish_changes">
                    Publish Changes
                    <Store className="h-4 w-4 ml-2" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" showClose={false}>
                  <DialogHeader>
                    <DialogTitle>
                      Are you sure you want to publish a new version of your
                      store?
                    </DialogTitle>
                    <DialogDescription>
                      Are you sure you want to go ahead?
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDialogOpen(false)}
                      className="flex items-center gap-2"
                    >
                      No, Cancel
                      <X className="h-4 w-4 text-red-600" />
                    </Button>
                    <SubmitButton
                      variant="outline"
                      size="sm"
                      onClick={handlePublishClick}
                      status={submitMutation.status}
                      className="w-auto flex items-center gap-2"
                      buttonTxt={
                        <>
                          Yes, Publish new version
                          <Check className="h-4 w-4 text-green-600" />
                        </>
                      }
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminAuthGuard>
  )
}

export default UnpublishedChangesBanner
